import { useEffect, useState } from "react";

import { useRootLoaderData } from "~/root";

/**
 * Transient top-of-viewport indicator that signals "this page was
 * rendered through the Builder branch of the catch-all dispatcher" (see
 * `app/routes/$.tsx`). Mounts when a Builder `page` entry wins URL
 * resolution — useful for diagnosing the case where an authored Builder
 * page hijacks a URL that would otherwise resolve to a PDP / PLP (the
 * "Builder first" override semantic from SFCC-10874).
 *
 * Shows the Builder entry id and links straight to the entry in the
 * Builder editor (`https://builder.io/content/{id}`) so a dev can jump
 * from the storefront to the source-of-truth entry in one click. Mounts
 * only on the client (avoids hydration mismatch) and auto-hides after
 * 10 seconds via a fade-out transition.
 *
 * Anchored just to the right of the bottom-left dev gear (same baseline,
 * height, and z-layer) so it reads as an extension of the gear and never
 * sits over top-of-page chrome — the alert bar in particular. For an
 * on-demand, page-wide view of every Builder region, use the Dev Panel's
 * Builder Inspector (`BuilderInspectorOverlay`) instead.
 *
 * Distinct from `BuilderPreviewBanner` (top-center yellow pill, fires when
 * `?builder.preview=` is in the URL — i.e. the Builder editor preview
 * iframe). This banner is purely a dispatcher-trace signal: it fires on
 * every Builder-served page render, preview or not.
 *
 * Dev-only: gated on `devConfig`, which the root loader sets to `null` when
 * the runtime `env.ENV === "production"` (root.tsx). This keys off the
 * deployment env rather than the build mode (`import.meta.env.MODE`), so the
 * banner stays visible on dev/preview deployments — which are built in
 * production mode too — but never appears on the production storefronts.
 */
export function BuilderPageFlashBanner({ pageId }: { pageId?: string }) {
  const { devConfig } = useRootLoaderData();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setVisible(true);
    const hideTimer = setTimeout(() => setVisible(false), 10_000);
    return () => clearTimeout(hideTimer);
  }, []);

  if (!devConfig || !mounted) return null;

  return (
    // Positioning wrapper: same bottom baseline + height + z-layer as the dev
    // gear (`bottom-4 left-4 h-10 w-10 z-[9999]`), anchored just to its right so
    // the pill reads as an extension of the gear. `pointer-events-none` keeps
    // the (transparent) wrapper from intercepting clicks while the pill is
    // faded out; the pill itself re-enables pointer events when visible.
    <div className="pointer-events-none fixed bottom-4 left-16 z-[9999] flex h-10 items-center">
      <div
        role="status"
        aria-live="polite"
        data-testid="builder-page-flash-banner"
        className={`rounded-full bg-yellow-400 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-black shadow-lg transition-opacity duration-500 ${
          visible
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        }`}
      >
        <span>Builder Page</span>
        {pageId ? (
          <>
            <span className="mx-2 opacity-50">·</span>
            <a
              href={`https://builder.io/content/${pageId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:no-underline"
            >
              Edit this page
            </a>
          </>
        ) : null}
      </div>
    </div>
  );
}
